<template>
    <div class="space-y-5">
        <div class="font-semibold flex flex-row items-center gap-1">
            {{ label }} <HelpButton v-if="helpButtonText" :text="helpButtonText" />
        </div>
        <div v-for="link in localLinks" :key="link.id">
            <LinkItem
                v-if="link.status !== 'delete'"
                :link="link"
                :error-messages="getErrorMessagesForLink(link.id)"
                :disabled-explanation="disabledExplanation"
                @input="handleInputEmit"
                @delete="handleDelete"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import HelpButton from '../HelpButton/HelpButton.vue';
import LinkItem from './LinkItem.vue';
import { emptyLink } from './multilink.js';
import { FormLink } from './types';

export type LinkFieldErrors = {
    name?: { _errors: string[] };
    to?: { _errors: string[] };
    description?: { _errors: string[] };
    status?: { _errors: string[] };
    id?: { _errors: string[] };
};

export type LinkArrayFormErrors = {
    _errors: string[];
    [index: number]: { _errors: string[] } & LinkFieldErrors;
};

export type MultiLinkProps = {
    links: FormLink[];
    label: string;
    helpButtonText?: string;
    errorMessages: LinkArrayFormErrors;
    disabledExplanation?: string;
};

const props = defineProps<MultiLinkProps>();
const emit = defineEmits<{
    (e: 'input', v: FormLink[]): void;
    (e: 'delete', v: FormLink[]): void;
}>();

const localLinks = ref(props.links);

function handleInputEmit(params: { event: Event; link: FormLink; fieldName: string }) {
    const { event, link, fieldName } = params;
    const target = event.target as HTMLInputElement;
    const value = target.value;
    // if last input is filled, add new one
    const nodes = props.links;
    const lastLink = nodes[nodes.length - 1];
    if (lastLink.name.length > 0 || lastLink.to.length > 0 || lastLink.description.length > 0) {
        localLinks.value = [...localLinks.value, emptyLink()];
    }
    // add updated field value for changed link
    const newStatus = link.status === 'create' ? ('create' as const) : ('update' as const);
    const updatedLink = { ...link, [fieldName]: value, status: newStatus };
    localLinks.value = localLinks.value.map((l) => (l.id === link.id ? updatedLink : l));
    emit('input', localLinks.value);
}

function handleDelete(link: FormLink) {
    const deletedLink = { ...link, status: 'delete' as const };
    localLinks.value = localLinks.value.map((l) => (l.id === link.id ? deletedLink : l));
    emit('delete', localLinks.value);
}

function getErrorMessagesForLink(id: string) {
    const index = localLinks.value.findIndex((l) => l.id === id);
    const entry = props.errorMessages?.[index];

    return {
        name: entry?.name?._errors ?? [],
        to: entry?.to?._errors ?? [],
        description: entry?.description?._errors ?? [],
    };
}
</script>
